import api from './api';

export interface TransactionSummary {
  // Old fields (for backward compatibility)
  totalEarned: string;
  availableToWithdraw: string;
  totalRefunds: string;
  totalFees: string;
  totalWithdrawals: string;
  // New fields from backend
  totalAmount?: string;
  completedAmount?: string;
  totalCheckouts?: number;
  completedCheckouts?: number;
  pendingCheckouts?: number;
  failedCheckouts?: number;
  totalOrders?: number;
  pendingOrders?: number;
  confirmedOrders?: number;
  completedOrders?: number;
  cancelledOrders?: number;
  // Calculated fields from backend
  eventiFee?: string; // Actual calculated fee based on vendor-specific percentages
  vendorPayouts?: string; // Total amount minus fees (what vendors receive)
  averageFeePercentage?: string; // Average fee percentage across all orders
}

export interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'payment' | 'refund' | 'withdrawal' | 'fee';
  status: 'completed' | 'processing' | 'failed' | 'pending';
  paymentId?: string;
  visaId?: string;
  eventId?: string;
  eventName?: string;
  eventDate?: string;
  vendorId?: string;
  vendorName?: string;
  orderId?: string;
  checkoutId?: string;
  // Customer details
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  // Payment method details
  paymentMethod?: 'mastercard' | 'visa' | 'apple-pay' | 'unknown';
  cardLast4?: string;
}

export interface OrderPayment {
  id: string;
  orderId: string;
  date: string;
  customer: string;
  phone: string;
  paymentMethod: 'mastercard' | 'visa' | 'apple-pay';
  paymentId?: string; // Payment ID from checkout
  cardLast4?: string;
  amount: string;
  eventId?: string;
  eventName?: string;
  vendorId?: string;
  vendorName?: string;
  feePercentage?: number; // Vendor-specific fee percentage
  feeAmount?: number; // Calculated fee amount
  vendorPayout?: number; // Amount vendor receives
}

export interface PayoutRequest {
  id: string;
  payoutRequestId: string;
  date: string;
  vendor: string;
  vendorId: string;
  description: string;
  amount: string;
  grossAmount?: number;
  feeAmount?: number;
  orderCount?: number;
  status: 'new' | 'paid' | 'failed';
  bankReference?: string;
  periodStart?: number;
  periodEnd?: number;
}

export interface PayoutRequestOrderDetail {
  orderId: string;
  amount: number;
  feePercentage: number;
  feeAmount: number;
  vendorPayoutAmount: number;
  completedAt?: number;
  status?: string;
  productId?: string;
  eventDetails?: {
    eventName?: string;
    eventDate?: string;
  };
  contactDetails?: {
    contactName?: string;
    phoneNumber?: string;
  };
}

export interface PayoutRequestDetail {
  payoutRequestId: string;
  vendorId: string;
  vendorName: string;
  status: 'new' | 'paid' | 'failed';
  amount: number;
  grossAmount: number;
  feeAmount: number;
  orderIds: string[];
  orderCount: number;
  periodStart: number;
  periodEnd: number;
  bankName?: string;
  bankAccountName?: string;
  bankAccountNumber?: string;
  bankReference?: string;
  paidAt?: string;
  paidByAdminId?: string;
  failureReason?: string;
  failedAt?: string;
  createdAt: number;
  updatedAt: string;
  orders: PayoutRequestOrderDetail[];
}

export interface PayoutRequestsSummary {
  totalAmount: string;
  paidAmount: string;
  pendingAmount: string;
  totalRequests: number;
  newRequests: number;
  paidRequests: number;
  failedRequests: number;
}

export interface TransactionsPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasMore: boolean;
  limit: number;
}

export interface TransactionsResponse {
  success: boolean;
  summary: TransactionSummary;
  count: number;
  data: Transaction[];
  pagination: TransactionsPagination;
}

export interface OrderPaymentsResponse {
  success: boolean;
  summary: TransactionSummary;
  count: number;
  data: OrderPayment[];
  pagination: TransactionsPagination;
}

export interface PayoutRequestsResponse {
  success: boolean;
  summary: PayoutRequestsSummary;
  count: number;
  data: PayoutRequest[];
  pagination: TransactionsPagination;
}

export interface MarkTransactionResponse {
  success: boolean;
  message: string;
  data: {
    checkoutId: string;
    paymentStatus: string;
    updatedAt: string;
    updatedBy: string;
    failureReason?: string;
  };
}

export interface OrderDetailsData {
  orderId: string;
  vendorId: string;
  productId: string;
  status: string;
  amount: number;
  discountAmount?: number;
  discountType?: string;
  startDateTime?: string;
  specialRequests?: string;
  packages?: any[];
  createdAt: number;
  updatedAt: number;
  customer?: {
    userId: string;
    name: string;
    phone: string;
    email?: string;
    imageProfile: string;
  };
  eventDetails?: {
    eventId: string;
    eventName?: string;
    eventDate?: string;
    eventAddress?: string;
    eventZone?: string;
    totalAmount: number;
    paidAmount: number;
  };
  contactDetails?: {
    contactName: string;
    phoneNumber: string;
    email?: string;
  };
  productDetails?: {
    productId: string;
    title: string;
  };
  vendorDetails?: {
    vendorId: string;
  };
}

export interface OrderDetailsResponse {
  success: boolean;
  data: {
    success: boolean;
    data: OrderDetailsData;
  };
}

export const transactionsService = {
  /**
   * Get all checkouts (for Payout Requests section)
   * Queries CHECKOUT table directly
   */
  async getAllCheckouts(
    page: number = 1,
    limit: number = 20,
    vendorId?: string,
    paymentStatus?: string,
    checkoutId?: string
  ): Promise<any> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (vendorId) params.append('vendorId', vendorId);
    if (paymentStatus) params.append('paymentStatus', paymentStatus);
    if (checkoutId) params.append('checkoutId', checkoutId);
    
    try {
      const response = await api.get(`/admins/checkouts?${params.toString()}`);
      console.log('getAllCheckouts raw response:', response);
      return response.data;
    } catch (error) {
      console.error('getAllCheckouts error:', error);
      throw error;
    }
  },

  /**
   * Get order payments (completed transactions)
   */
  async getOrderPayments(
    page: number = 1,
    limit: number = 20,
    status?: string
  ): Promise<OrderPaymentsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (status) params.append('status', status);
    
    const response = await api.get(`/admins/transactions?${params.toString()}`);
    
    // Transform transactions to order payments format
    const orderPayments: OrderPayment[] = response.data.data
      .filter((t: Transaction) => t.type === 'payment')
      .map((t: Transaction) => ({
        id: t.id,
        orderId: t.orderId || 'N/A',
        date: new Date(t.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
        customer: t.customerName || 'Unknown Customer',
        phone: t.customerPhone || '+974 XXX XXX XXXX',
        paymentMethod: t.paymentMethod || 'mastercard',
        cardLast4: t.cardLast4,
        amount: `QAR ${t.amount.toFixed(2)}`,
        eventId: t.eventId,
        eventName: t.eventName,
        vendorId: t.vendorId,
        vendorName: t.vendorName,
      }));
    
    return {
      success: response.data.success,
      summary: response.data.summary,
      count: orderPayments.length,
      data: orderPayments,
      pagination: response.data.pagination,
    };
  },

  /**
   * Get vendor payout requests (paginated)
   */
  async getPayoutRequests(
    page: number = 1,
    limit: number = 20,
    vendorId?: string,
    status?: string
  ): Promise<any> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (vendorId) params.append('vendorId', vendorId);
    if (status) params.append('status', status);

    const response = await api.get(`/admins/payout-requests?${params.toString()}`);
    return response.data;
  },

  /**
   * Get a single payout request with included orders
   */
  async getPayoutRequestDetails(payoutRequestId: string): Promise<{ success: boolean; data: PayoutRequestDetail }> {
    const response = await api.get(`/admins/payout-requests/${payoutRequestId}`);
    return response.data;
  },

  /**
   * Mark a payout request as paid with a bank/transaction reference
   */
  async markPayoutRequestPaid(
    payoutRequestId: string,
    bankReference: string
  ): Promise<any> {
    const response = await api.post(`/admins/payout-requests/${payoutRequestId}/mark-paid`, {
      bankReference,
    });
    return response.data;
  },

  /**
   * Mark a payout request as failed
   */
  async markPayoutRequestFailed(
    payoutRequestId: string,
    reason: string
  ): Promise<any> {
    const response = await api.post(`/admins/payout-requests/${payoutRequestId}/mark-failed`, {
      reason,
    });
    return response.data;
  },

  /**
   * Mark a checkout transaction as paid (completed)
   */
  async markTransactionPaid(checkoutId: string): Promise<MarkTransactionResponse> {
    const response = await api.post(`/admins/transactions/${checkoutId}/mark-paid`);
    return response.data;
  },

  /**
   * Mark a checkout transaction as failed
   */
  async markTransactionFailed(checkoutId: string, reason: string): Promise<MarkTransactionResponse> {
    const response = await api.post(`/admins/transactions/${checkoutId}/mark-failed`, {
      reason,
    });
    return response.data;
  },

  /**
   * Get order details
   */
  async getOrderDetails(orderId: string): Promise<OrderDetailsResponse> {
    const response = await api.get(`/admins/orders/${orderId}`);
    return response.data;
  },
};
